"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import { canOptimizeImage } from "@/lib/image";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPhoto,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import type { EventPhoto } from "./types";
import { BlockTitle, UI, useText } from "./shared";

/** Responsive photo grid: 2 → 3 → 4 → 5 columns; `initial` cards, then "View more". */
export function EventPhotoGallery({
  items,
  title,
  initial = 10,
  step = 10,
}: {
  items: EventPhoto[];
  title?: string;
  initial?: number;
  step?: number;
}) {
  const { text } = useText();
  const [visible, setVisible] = useState(initial);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  if (items.length === 0) return null;
  const shown = items.slice(0, visible);

  return (
    <div>
      <BlockTitle icon={<IconPhoto className="size-5" aria-hidden="true" />}>
        {title ?? text(UI.photoHighlights)}
      </BlockTitle>
      <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
        {shown.map((photo, i) => (
          <li key={`${photo.src}-${i}`}>
            <EventPhotoCard photo={photo} onOpen={() => setOpenIndex(i)} />
          </li>
        ))}
      </ul>
      {visible < items.length && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + step)}
            className="rounded-full border border-[var(--event-accent)] px-6 py-2.5 text-sm font-semibold text-[var(--event-accent)] transition hover:bg-[#f6cf63]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63]"
          >
            {text(UI.viewMore)} ({items.length - visible})
          </button>
        </div>
      )}
      {openIndex !== null && (
        <PhotoLightbox
          items={shown}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </div>
  );
}

export function EventPhotoCard({
  photo,
  onOpen,
}: {
  photo: EventPhoto;
  onOpen: () => void;
}) {
  const { text } = useText();
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={text(photo.alt)}
      className="group relative block aspect-[4/3] w-full overflow-hidden rounded-[14px] border border-white/10 bg-[#0a1f4f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63]"
    >
      <Image
        src={photo.src}
        unoptimized={!canOptimizeImage(photo.src)}
        alt={text(photo.alt)}
        fill
        sizes="(min-width: 1280px) 240px, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
        quality={75}
        className="object-cover transition duration-500 ease-out motion-safe:group-hover:scale-105"
      />
      <span className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(3,16,45,0.75))] opacity-80 transition group-hover:opacity-100" />
    </button>
  );
}

export function PhotoLightbox({
  items,
  index,
  onIndexChange,
  onClose,
}: {
  items: EventPhoto[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const { text } = useText();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const touchX = useRef<number | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const photo = items[index];
  const go = (delta: number) => {
    setZoomed(false);
    onIndexChange((index + delta + items.length) % items.length);
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = previous;
    };
  }, []);

  if (!photo) return null;
  return (
    <dialog
      ref={dialogRef}
      aria-label={text(photo.alt)}
      onClose={onClose}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") go(1);
        if (event.key === "ArrowLeft") go(-1);
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) dialogRef.current?.close();
      }}
      className="m-0 h-dvh max-h-none w-screen max-w-none bg-[#020a1c]/95 p-0 text-white backdrop:bg-black/70"
    >
      <div
        className="flex h-full flex-col"
        onTouchStart={(event) => {
          touchX.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const end = event.changedTouches[0]?.clientX;
          if (
            !zoomed &&
            touchX.current !== null &&
            end !== undefined &&
            Math.abs(touchX.current - end) > 50
          )
            go(touchX.current > end ? 1 : -1);
          touchX.current = null;
        }}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <p className="text-sm text-white/70">
            {index + 1} / {items.length}
          </p>
          <div className="flex gap-2">
            <LightboxButton
              label={text(zoomed ? UI.zoomOut : UI.zoomIn)}
              pressed={zoomed}
              onClick={() => setZoomed((z) => !z)}
            >
              {zoomed ? (
                <IconZoomOut className="size-5" />
              ) : (
                <IconZoomIn className="size-5" />
              )}
            </LightboxButton>
            <LightboxButton
              label={text(UI.close)}
              onClick={() => dialogRef.current?.close()}
            >
              <IconX className="size-5" />
            </LightboxButton>
          </div>
        </div>
        <div
          className={cn(
            "relative min-h-0 flex-1",
            zoomed ? "overflow-auto" : "overflow-hidden",
          )}
        >
          <div
            className={cn(
              "relative mx-auto h-full",
              zoomed ? "w-[200%] md:w-[160%]" : "w-full",
            )}
          >
            <Image
              key={photo.src}
              src={photo.src}
              unoptimized={!canOptimizeImage(photo.src)}
              alt={text(photo.alt)}
              fill
              sizes="100vw"
              quality={85}
              className="object-contain"
            />
          </div>
          {items.length > 1 && (
            <>
              <LightboxButton
                label={text(UI.previous)}
                onClick={() => go(-1)}
                className="absolute left-3 top-1/2 -translate-y-1/2"
              >
                <IconChevronLeft className="size-6" />
              </LightboxButton>
              <LightboxButton
                label={text(UI.next)}
                onClick={() => go(1)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <IconChevronRight className="size-6" />
              </LightboxButton>
            </>
          )}
        </div>
        {/* No caption: CMS photo titles are camera file names (e.g. AV1_5157). */}
        <div className="h-6 shrink-0" />
      </div>
    </dialog>
  );
}

function LightboxButton({
  label,
  onClick,
  children,
  pressed,
  className,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "inline-flex size-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63]",
        className,
      )}
    >
      {children}
    </button>
  );
}
